/**
 * Checkout bar helpers (#956).
 *
 * The plans page and the signup plan step show a sticky checkout bar once a
 * plan or add-on change is selected. These pure helpers decide what the bar
 * says and keep the selection for the browser session, so the page, the bar
 * and their specs share one set of rules and words. Nothing here charges,
 * prices or checks out: the pages call their existing handlers.
 */

/**
 * What the bar's primary button does:
 * - `new`: a first paid subscription, through Stripe checkout;
 * - `change`: a change to an existing plan (a new plan or add-ons);
 * - `quote`: a plan that sales quotes (Enterprise, or unadvertised prices).
 */
export type CheckoutBarMode = 'new' | 'change' | 'quote';

/** Everything the checkout bar shows. Strings are ready to display. */
export interface CheckoutBarSummary {
  mode: CheckoutBarMode;
  /** "Standard plan" or "Coin pack: 1,000 Coins a month". */
  title: string;
  /** "Billed annually", or null when there is no interval to show. */
  interval: string | null;
  /** Names of the selected add-ons, in order. */
  addOns: string[];
  /** What is charged at checkout, after any code or sale, e.g. "$6,000". */
  priceNow: string | null;
  /** The regular price, struck through, only while a code or sale applies. */
  regularPrice: string | null;
  /** "then $1,000/month" for a code that only discounts the first payment. */
  thenText: string | null;
  /** Shown instead of a price, e.g. "Quoted by our team". */
  priceNote: string | null;
}

/** The primary button's words for each mode. */
export const CHECKOUT_PRIMARY_LABELS: Readonly<Record<CheckoutBarMode, string>> = {
  new: 'Continue to secure checkout',
  change: 'Review and confirm changes',
  quote: 'Request a quote',
};

/**
 * The primary button's words.
 *
 * @param mode What the button does.
 * @returns The label, in sentence case.
 */
export function checkoutPrimaryLabel(mode: CheckoutBarMode): string {
  return CHECKOUT_PRIMARY_LABELS[mode];
}

/**
 * The line under a discounted price: how long the discount lasts and what it
 * costs after. "then $1,000/month" for a code on the first payment only.
 *
 * @param months Promotion length in months; 0 for the first payment only,
 *   Infinity for the life of the plan.
 * @param listPrice The regular price with its interval, e.g. "$1,000/month".
 * @returns The note, or null when the discount never ends.
 */
export function promoThenText(months: number, listPrice: string): string | null {
  if (!Number.isFinite(months)) return null;
  if (months <= 0) return `then ${listPrice}`;
  if (months === 1) return `for the first month, then ${listPrice}`;
  return `for ${months} months, then ${listPrice}`;
}

/** Roles that may buy a plan or Coin pack (mirrors the API's billing roles). */
export const PURCHASE_ROLES: readonly string[] = ['admin', 'owner', 'super_admin'];

/**
 * Whether this user may buy for their company. Platform admins hold the
 * `adminRole` claim rather than a company role, so it counts too.
 *
 * @param user The signed-in user, or nothing.
 * @returns True when the checkout bar should be offered.
 */
export function canPurchaseForCompany(
  user: { role?: string | null; adminRole?: string | null } | null | undefined,
): boolean {
  if (!user) return false;
  if (user.adminRole === 'super_admin') return true;
  return PURCHASE_ROLES.includes(String(user.role ?? ''));
}

/** A selection kept for the browser session. */
export interface StoredCheckoutSelection {
  planId: string | null;
  interval: 'monthly' | 'annual';
  addOnIds: string[];
  packId: string | null;
  promoCode: string | null;
}

const STORAGE_PREFIX = 'sn.checkout-selection.';

function sessionStore(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Keeps the selection for this browser session, per company.
 *
 * @param scope The company id (or "signup" before one exists).
 * @param selection What is selected.
 */
export function saveCheckoutSelection(scope: string, selection: StoredCheckoutSelection): void {
  try {
    sessionStore()?.setItem(STORAGE_PREFIX + scope, JSON.stringify(selection));
  } catch {
    // Storage full or blocked: the selection just isn't remembered.
  }
}

/**
 * Reads a kept selection back, or null when there is none or it is unreadable.
 *
 * @param scope The company id used when it was saved.
 */
export function readCheckoutSelection(scope: string): StoredCheckoutSelection | null {
  try {
    const raw = sessionStore()?.getItem(STORAGE_PREFIX + scope);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredCheckoutSelection>;
    return {
      planId: typeof parsed.planId === 'string' ? parsed.planId : null,
      interval: parsed.interval === 'monthly' ? 'monthly' : 'annual',
      addOnIds: Array.isArray(parsed.addOnIds)
        ? parsed.addOnIds.filter((id): id is string => typeof id === 'string')
        : [],
      packId: typeof parsed.packId === 'string' ? parsed.packId : null,
      promoCode: typeof parsed.promoCode === 'string' ? parsed.promoCode : null,
    };
  } catch {
    return null;
  }
}

/**
 * Forgets a kept selection (after checkout starts, or "Clear selection").
 *
 * @param scope The company id used when it was saved.
 */
export function clearCheckoutSelection(scope: string): void {
  try {
    sessionStore()?.removeItem(STORAGE_PREFIX + scope);
  } catch {
    // Nothing to clear.
  }
}
